import { Loader2 } from "lucide-react";
import { type ChangeEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { ApiError, api } from "../../../lib/api";
import { cn } from "../../../lib/utils";

export interface AddressSearchResult {
  id: string;
  address: string;
  suburb: string;
  city: string;
  lat: number;
  lng: number;
}

interface AddressAutocompleteProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onSelect: (result: { address: string; lat: number; lng: number }) => void;
  placeholder?: string;
  biasLatitude?: number | null;
  biasLongitude?: number | null;
}

interface GeoBias {
  lat: number;
  lng: number;
}

const DEBOUNCE_MS = 250;
const MIN_CHARS = 3;
const GEOLOCATION_TIMEOUT_MS = 2000;
const BIAS_CACHE_KEY = "address.bias";
const BIAS_CACHE_TTL_MS = 12 * 60 * 60 * 1000;
const DEFAULT_BIAS: GeoBias = { lat: -36.8485, lng: 174.7633 };

interface CachedBias extends GeoBias {
  cachedAt: number;
}

function readCachedBias(): GeoBias | null {
  try {
    const raw = window.localStorage.getItem(BIAS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedBias;
    if (Date.now() - parsed.cachedAt > BIAS_CACHE_TTL_MS) return null;
    return { lat: parsed.lat, lng: parsed.lng };
  } catch {
    return null;
  }
}

function writeCachedBias(bias: GeoBias): void {
  try {
    const cached: CachedBias = { ...bias, cachedAt: Date.now() };
    window.localStorage.setItem(BIAS_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // Ignore storage failures (private browsing, quota, etc); fail silently.
  }
}

function resolveFallbackBias(onResolved: (bias: GeoBias) => void): void {
  const cached = readCachedBias();
  if (cached) {
    onResolved(cached);
    return;
  }

  if (!navigator.geolocation) {
    onResolved(DEFAULT_BIAS);
    return;
  }

  let settled = false;
  const settleOnce = (bias: GeoBias): void => {
    if (settled) return;
    settled = true;
    onResolved(bias);
  };

  navigator.geolocation.getCurrentPosition(
    (position) => {
      const bias = { lat: position.coords.latitude, lng: position.coords.longitude };
      writeCachedBias(bias);
      settleOnce(bias);
    },
    () => settleOnce(DEFAULT_BIAS),
    { timeout: GEOLOCATION_TIMEOUT_MS },
  );

  // Belt-and-braces timeout in case the browser never calls either callback.
  setTimeout(() => settleOnce(DEFAULT_BIAS), GEOLOCATION_TIMEOUT_MS);
}

export function AddressAutocomplete({
  id,
  value,
  onChange,
  onSelect,
  placeholder,
  biasLatitude,
  biasLongitude,
}: AddressAutocompleteProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<AddressSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [fallbackBias, setFallbackBias] = useState<GeoBias>(DEFAULT_BIAS);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const biasRef = useRef<GeoBias>(DEFAULT_BIAS);

  // Resolve the geolocation/default fallback bias once on mount, not per keystroke.
  useEffect(() => {
    resolveFallbackBias(setFallbackBias);
  }, []);

  useEffect(() => {
    biasRef.current =
      biasLatitude != null && biasLongitude != null ? { lat: biasLatitude, lng: biasLongitude } : fallbackBias;
  }, [biasLatitude, biasLongitude, fallbackBias]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const search = (query: string): void => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.trim().length < MIN_CHARS) {
      requestIdRef.current += 1;
      setResults([]);
      setLoading(false);
      setError(null);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      setOpen(true);

      const bias = biasRef.current;
      const searchParams = new URLSearchParams({
        q: query,
        lat: String(bias.lat),
        lng: String(bias.lng),
      });

      api
        .get<{ results: AddressSearchResult[] }>(`/api/address/search?${searchParams.toString()}`)
        .then((data) => {
          if (requestIdRef.current !== requestId) return;
          setResults(data.results);
          setHighlightedIndex(-1);
        })
        .catch((err: unknown) => {
          if (requestIdRef.current !== requestId) return;
          setError(err instanceof ApiError ? err.message : "Address lookup failed");
          setResults([]);
        })
        .finally(() => {
          if (requestIdRef.current === requestId) setLoading(false);
        });
    }, DEBOUNCE_MS);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const next = e.target.value;
    onChange(next);
    search(next);
  };

  const selectResult = (result: AddressSearchResult): void => {
    onChange(result.address);
    onSelect({ address: result.address, lat: result.lat, lng: result.lng });
    setOpen(false);
    setResults([]);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (!open || results.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      if (highlightedIndex >= 0) {
        e.preventDefault();
        const result = results[highlightedIndex];
        if (result) selectResult(result);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <input
          id={id}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) setOpen(true);
          }}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="address-autocomplete-input"
        />
        {loading && (
          <Loader2
            className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
            data-testid="address-autocomplete-loading"
          />
        )}
      </div>
      {open && (
        <div
          className="absolute z-20 mt-1 w-full rounded-md border bg-white shadow-lg max-h-64 overflow-y-auto"
          data-testid="address-autocomplete-list"
        >
          {loading && (
            <p className="px-3 py-2 text-sm text-muted-foreground" data-testid="address-autocomplete-searching">
              Searching...
            </p>
          )}
          {!loading && error && (
            <p className="px-3 py-2 text-sm text-destructive" data-testid="address-autocomplete-error">
              {error}
            </p>
          )}
          {!loading && !error && results.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground" data-testid="address-autocomplete-empty">
              No matching addresses.
            </p>
          )}
          {!loading &&
            !error &&
            results.map((result, index) => (
              <button
                key={result.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectResult(result)}
                className={cn(
                  "block w-full px-3 py-2 text-left text-sm hover:bg-muted",
                  index === highlightedIndex && "bg-muted",
                )}
                data-testid={`address-autocomplete-option-${result.id}`}
              >
                {result.address}
                {(result.suburb || result.city) && (
                  <span className="block text-xs text-muted-foreground">
                    {[result.suburb, result.city].filter(Boolean).join(", ")}
                  </span>
                )}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
